import { DigitSpanVariant, SartErrorType, VisualReactionErrorType } from '@/gql/enums';
import type { SartAssessmentResultDataFragment, VisualReactionAssessmentResultDataFragment } from '@/gql/graphql';
import { splitTextWithConjunctions } from '@/utils/common';

export interface FilterState {
    programId: string | null;
    assessmentRunId: string | null;
}

export interface ML360Statistic {
    label: string;
    shortLabel: string;
    value: number;
    total: number;
}

export interface SelfDeterminationStatistic {
    domainMax: number;
    radarData: { shortLabel: string; value: number }[];
    summaryCards: { label: string; value: number; total: number }[];
}

export interface TeamResilienceStatistic {
    domainMax: number;
    radarData: { shortLabel: string; value: number }[];
    summaryCards: { label: string; value: number; total: number }[];
    tickCount: number;
}

export interface SpatialPlanningResultStatistic {
    correctAnswers: number;
    incorrectAnswers: number;
    accuracy: number;
    averageResponseTimeMs: number;
    fastestResponseTimeMs: number;
}

export interface SartTrialChartData {
    name: string;
    data: { index: number; responseTimeMs: number }[];
}

export interface SartStatistics {
    series: SartTrialChartData[];
    line: SartTrialChartData;
    accuracy: number;
    averageResponseTimeMs: number;
    fastestResponseTimeMs: number;
}

export interface VisualReactionTrialChartData {
    question: number;
    responseTimeMs: number;
    isCorrect: boolean;
    isMissedResponse: boolean;
}

export interface VisualReactionRoundChartData {
    trials: VisualReactionTrialChartData[];
    averageResponseTimeMs: number;
    accuracy: number;
    roundIndex: number;
    fastestTimeMs: number;
}

export interface VisualReactionStatistics {
    rounds: VisualReactionRoundChartData[];
    accuracy: number;
    averageResponseTimeMs: number;
    fastestResponseTimeMs: number;
}

/**
 * Build chart-ready SART statistics from a backend-returned result fragment.
 * Aggregates (averageResponseTimeMs / fastestResponseTimeMs) come straight from
 * the fragment; accuracy and chart series are derived from `trials`.
 *
 * Accepts `SartAssessmentResultDataFragment`; structurally identical fragments
 * (e.g. `ResultSartResultFragment`) satisfy the input via TypeScript's
 * structural typing.
 */
export const buildSartStatistics = (result: SartAssessmentResultDataFragment): SartStatistics => {
    const { trials, correctHits, averageResponseTimeMs, fastestResponseTimeMs } = result;
    const accuracy = trials.length > 0 ? Number(((correctHits / trials.length) * 100).toFixed(2)) : 0;

    const { correct, incorrect, missed } = trials.reduce(
        (
            acc: {
                correct: { index: number; responseTimeMs: number }[];
                incorrect: { index: number; responseTimeMs: number }[];
                missed: { index: number; responseTimeMs: number }[];
            },
            trial
        ) => {
            if (trial.isCorrect) {
                acc.correct.push({ index: trial.index + 1, responseTimeMs: trial.responseTimeMs });
            } else if (trial.errorType === SartErrorType.Incorrect) {
                acc.incorrect.push({ index: trial.index + 1, responseTimeMs: trial.responseTimeMs });
            } else if (trial.errorType === SartErrorType.Missed) {
                acc.missed.push({ index: trial.index + 1, responseTimeMs: trial.responseTimeMs });
            }
            return acc;
        },
        { correct: [], incorrect: [], missed: [] }
    );

    return {
        accuracy,
        averageResponseTimeMs,
        fastestResponseTimeMs,
        series: [
            { name: 'correct', data: correct },
            { name: 'incorrect', data: incorrect },
            { name: 'missed', data: missed },
        ],
        line: {
            name: 'line',
            data: trials.map(trial => ({
                index: trial.index + 1,
                responseTimeMs: trial.responseTimeMs,
            })),
        },
    };
};

/**
 * Build chart-ready Visual Reaction statistics from a backend-returned result
 * fragment. Per-round and overall aggregates come straight from the fragment;
 * per-round `fastestTimeMs` is derived from question response times.
 *
 * Accepts `VisualReactionAssessmentResultDataFragment`; structurally identical
 * fragments (e.g. `ResultVisualReactionResultFragment`) satisfy the input via
 * TypeScript's structural typing.
 */
export const buildVisualReactionStatistics = (
    result: VisualReactionAssessmentResultDataFragment
): VisualReactionStatistics => ({
    rounds: result.rounds.map((round, roundIndex) => {
        const roundResponseTimes = round.questions.filter(q => q.responseTimeMs > 0).map(q => q.responseTimeMs);

        return {
            trials: round.questions.map((q, questionIndex) => ({
                question: questionIndex + 1,
                responseTimeMs: Math.round(q.responseTimeMs),
                isCorrect: q.isCorrect,
                isMissedResponse: q.errorType === VisualReactionErrorType.Missed,
            })),
            accuracy: round.accuracyPerRound,
            averageResponseTimeMs: round.averageResponseTimeMsPerRound,
            roundIndex,
            fastestTimeMs: roundResponseTimes.length > 0 ? Math.min(...roundResponseTimes) : 0,
        };
    }),
    accuracy: result.accuracy,
    averageResponseTimeMs: result.averageResponseTimeMs,
    fastestResponseTimeMs: result.fastestResponseTimeMs,
});

export interface ProcessingSpeedTrialChartData {
    index: number;
    responseTimeMs: number;
    block: number;
}

export interface ProcessingSpeedBlockChartData {
    block: number;
    totalTrials: number;
    correctCount: number;
    accuracy: number;
    medianRT: number;
    commissions: number;
    omissions: number;
}

export interface ProcessingSpeedChartSeries {
    name: 'correct' | 'incorrect' | 'timeout';
    data: ProcessingSpeedTrialChartData[];
}

export interface ProcessingSpeedStatistics {
    accuracy: number;
    averageResponseTimeMs: number;
    blockSummaries: ProcessingSpeedBlockChartData[];
    totalCommissions: number;
    totalOmissions: number;
    chartSeries: ProcessingSpeedChartSeries[];
    totalTrials: number;
}

export const defaultMaxScore = 5;

/**
 * Computes a readable tick count for the radar chart's radial axis.
 * Uses finer steps for small domains and coarser steps for large domains to avoid label clutter.
 */
export const computeRadarTickCount = (domainMax: number): number => {
    if (domainMax <= 10) {
        return domainMax + 1;
    }

    const step = domainMax >= 100 ? 25 : 20;
    return Math.floor(domainMax / step) + 1;
};

export const convertShortLabel = (label: string): string => {
    switch (label) {
        case 'Demonstrate Self and Situational Awareness':
            return 'Awareness';
        case 'Exude Calm and Patience':
            return 'Calm & Patience';
        case 'Adopt an Open Mind':
            return 'Open Mind';
        case 'Maintain Focus and Presence of Mind':
            return 'Focus & Presence';
        case 'Seek New Ideas and Learning':
            return 'Growth';
        default:
            return label;
    }
};

// Create a reusable tick renderer for PolarAngleAxis labels that adds
// an outward radial offset to improve spacing between the radar and labels.
export const createRadialLabelTickRenderer = (
    offsetPx: number,
    options?: { fontSize?: number; fill?: string; fontWeight?: number | string; wrapWords?: boolean }
) => {
    const { fontSize = 12, fill = 'var(--color-neutral-500)', fontWeight = 500, wrapWords = false } = options || {};
    const RadialLabelTickRenderer = (props: any) => {
        const { x, y, cx, cy, payload, textAnchor } = props;
        const dx = x - cx;
        const dy = y - cy;
        const distance = Math.sqrt(dx * dx + dy * dy) || 1;
        const nx = x + (dx / distance) * offsetPx;
        const ny = y + (dy / distance) * offsetPx;

        if (wrapWords && payload.value) {
            const text = String(payload.value);
            const lines = splitTextWithConjunctions(text);

            const lineHeight = fontSize * 1.5;
            const totalHeight = lines.length * lineHeight;
            const startY = ny - (totalHeight - lineHeight) / 2;

            return (
                <text x={nx} y={startY} fill={fill} fontSize={fontSize} fontWeight={fontWeight} textAnchor={textAnchor}>
                    {lines.map((line: string, index: number) => (
                        <tspan key={index} x={nx} dy={index === 0 ? 0 : lineHeight}>
                            {line}
                        </tspan>
                    ))}
                </text>
            );
        }

        return (
            <text x={nx} y={ny} fill={fill} fontSize={fontSize} fontWeight={fontWeight} textAnchor={textAnchor}>
                {payload.value}
            </text>
        );
    };

    return RadialLabelTickRenderer;
};

/**
 * Get the translated label for a DigitSpan variant.
 */
export const digitSpanVariantLabel = (variant: DigitSpanVariant, t: (key: string) => string): string => {
    switch (variant) {
        case DigitSpanVariant.Forward:
            return t('trial.variant.forward');
        case DigitSpanVariant.Backward:
            return t('trial.variant.backward');
        case DigitSpanVariant.Sequencing:
            return t('trial.variant.sequencing');
        default:
            return t('trial.variant.unknown');
    }
};

/**
 * Get the appropriate Tailwind CSS classes for an AiT level badge.
 */
export const getAiTLevelColor = (level: number): string => {
    switch (level) {
        case 5:
            return 'text-green-600 bg-green-100 border-green-600';
        case 4:
            return 'text-green-500 bg-green-50 border-green-500';
        case 3:
            return 'text-amber-500 bg-amber-50 border-amber-500';
        case 2:
            return 'text-orange-500 bg-orange-50 border-orange-500';
        default:
            return 'text-red-500 bg-red-50 border-red-500';
    }
};

/**
 * Get the translated title for DigitSpan results based on variants.
 * If multiple variants: "Digit Span Results"
 * If single variant: "{Variant} Digit Span Results" (e.g., "Backward Digit Span Results")
 */
export const getDigitSpanResultsTitle = (variants: DigitSpanVariant[], t: (key: string) => string): string => {
    if (variants.length !== 1) {
        return t('results.title.multiple');
    }

    const variant = variants[0];
    switch (variant) {
        case DigitSpanVariant.Forward:
            return t('results.title.forward');
        case DigitSpanVariant.Backward:
            return t('results.title.backward');
        case DigitSpanVariant.Sequencing:
            return t('results.title.sequencing');
        default:
            return t('results.title.multiple');
    }
};
